import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Grid, H, Image, P, Wrapper } from '@farewill/ui'
import { BREAKPOINT, COLOR, FONT, GTR } from '@farewill/ui/tokens'
import { screenMax } from '@farewill/ui/helpers/responsive'

import SimpleQuote from 'components/SimpleQuote'
import AspectRatioWrapper from 'components/AspectRatioWrapper'
import BreakpointSwitch from 'components/BreakpointSwitch'
import TrustpilotSummary from 'components/TrustpilotWidget/TrustpilotSummary'
import { CUSTOMER_QUOTE_TYPES } from 'views/general/templates/LocalFuneralPage/constants'

const StyledWrapper = styled.div`
  background: ${COLOR.ACCENT.PRIMARY_20};
  padding-top: 100px;
  padding-bottom: 100px;

  ${screenMax.l`
    padding-top: 70px;
    padding-bottom: 80px;
  `}
`

const StyledGrid = styled(Grid)`
  align-items: center;
`

const StyledGridItem = styled(Grid.Item)`
  ${screenMax.m`
    grid-row-start: 2;
  `}
`

const StyledFigure = styled.figure`
  margin: 0;

  img {
    object-fit: cover;
  }

  ${screenMax.l`
    img {
      height: 100%;
      object-position: top;
    }
  `}
`

const StyledSimpleQuote = styled(SimpleQuote)`
  color: ${COLOR.BLACK};
  font-size: ${FONT.SIZE.L};
`

const StyledAuthor = styled(P)`
  text-transform: uppercase;
  font-weight: ${FONT.WEIGHT.BOLD};
  font-size: ${FONT.SIZE.XS};
  letter-spacing: 1px;
`

const StyledFigcaption = styled.figcaption`
  font-size: ${FONT.SIZE.S};
  color: ${COLOR.BLACK};
  margin-top: ${GTR.XS};
`

const StyledH = styled(H)`
  ${screenMax.s`
    font-size: ${FONT.SIZE.XL};
  `}
`

const Quote = ({ type, author, quote, title, image, caption }) => (
  <StyledWrapper>
    <StyledGrid container containerPaddingTop="0" containerPaddingBottom="0">
      <StyledGridItem
        startColumnFromM={2}
        spanFromM={10}
        startColumnFromL={1}
        spanFromL={5}
      >
        <StyledFigure>
          <BreakpointSwitch
            aboveComponent={
              <AspectRatioWrapper ratio={1 / 1}>
                <Image
                  path={image}
                  ext="jpg"
                  width={600}
                  height="100%"
                  stretch
                />
              </AspectRatioWrapper>
            }
            belowComponent={
              <AspectRatioWrapper ratio={16 / 11}>
                <Image
                  path={image}
                  width={600}
                  ext="jpg"
                  widthFromS={900}
                  stretch
                />
              </AspectRatioWrapper>
            }
            breakAt={BREAKPOINT.M}
          />
        </StyledFigure>
        {caption && <StyledFigcaption>{caption}</StyledFigcaption>}
      </StyledGridItem>
      <Grid.Item
        spanFromM={10}
        startColumnFromM={2}
        spanFromL={6}
        startColumnFromL={6}
        spanFromXL={6}
        startColumnFromXL={7}
      >
        {title && (
          <StyledH tag="h2" size="M" decorative maxWidth={480}>
            {title}
          </StyledH>
        )}

        <StyledSimpleQuote decorative={type === CUSTOMER_QUOTE_TYPES.ORGANIC}>
          {quote}
        </StyledSimpleQuote>

        <StyledAuthor color={COLOR.BLACK} margin={['L', 0, 0]}>
          {author}
        </StyledAuthor>

        <Wrapper margin={['XL', 0, 0]} marginFromL={['L', 0, 0]}>
          <TrustpilotSummary hideReviewNumber />
        </Wrapper>
      </Grid.Item>
    </StyledGrid>
  </StyledWrapper>
)

Quote.propTypes = {
  image: PropTypes.string.isRequired,
  author: PropTypes.string.isRequired,
  quote: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.values(CUSTOMER_QUOTE_TYPES)),
  title: PropTypes.string,
  caption: PropTypes.string,
}

Quote.defaultProps = {
  type: 'customer',
  title: null,
  caption: null,
}

export default Quote
